import { Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from '../CreateOrEditCategoryModal/styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { LevelDefinition } from '../../../api/types/progress';
import { MinimalLevelDefinitionSchema } from '../../../api/progress/parser';
import { CreateLevelDefinitionDto } from '../../../api/typeDtos/progress';
import { createLevelDefinition, updateLevelDefinition } from '../../../api/progress';
import { textFieldUtils } from '../../../utils/textFields';

const initialValues = {
  level: '',
  title: '',
  minPoints: '',
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: LevelDefinition;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const levelInitialValues = {
    level: modalState?.level,
    title: modalState?.title,
    minPoints: modalState?.minPoints,
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      level: Number(values.level),
      minPoints: Number(values.minPoints),
    });
  };

  return (
    <Formik
      initialValues={modalState ? levelInitialValues : initialValues}
      validationSchema={MinimalLevelDefinitionSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Level</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.level?.toString() ?? ''}
                    onChangeText={handleChange}
                    name={'level'}
                    disabled={!!modalState}
                    errorMessage={errors.level && touched.level ? errors?.level : undefined}
                    placeholder={'e.g. 1'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.title}
                    onChangeText={handleChange}
                    name={'title'}
                    errorMessage={errors.title && touched.title ? errors?.title : undefined}
                    placeholder={'e.g. Level 1'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Minimum points</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.minPoints?.toString() ?? ''}
                    onChangeText={handleChange}
                    name={'minPoints'}
                    errorMessage={errors.minPoints && touched.minPoints ? errors?.minPoints : undefined}
                    placeholder={'e.g. 0'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditLevelDefinitionModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { level?: LevelDefinition };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateLevelDefinitionDto = {
      level: values?.level,
      title: values?.title,
      minPoints: values?.minPoints,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.level?.id
        ? await updateLevelDefinition({ ...data, id: modalState?.level?.id })
        : await createLevelDefinition(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.level?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a level');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.level} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.level ? `Edit level` : 'Create new level'}
        titleSuffix={modalState?.level ? `(ID: ${modalState?.level?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditLevelDefinitionModal;
